import {Row} from '@tanstack/react-table';
import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {ELoading} from '@ELib/components/ETable/ELoading';

import {getColSpan, TBodyRow} from './TBodyRow';
import {useActionsHandlerForSubRow} from './hooks';
import {
  ETableActiveItem,
  ETableRow,
  ETableRowActionHandler,
  ETableRowClickHandler,
  ETableRowExpansion,
  ETableSubRow,
  isCellObject,
} from './types';

/** Resolves the potentially async expansion content of the parent row. */
function useSubRowsContent(
  parentRow: Row<ETableRow>,
  onContentResized?: () => void
): {isLoading: boolean; subRows: ETableRowExpansion | undefined} {
  const [isLoading, setIsLoading] = useState(false);
  const [subRows, setSubRows] = useState<ETableRowExpansion>();

  // notify about height changes caused by the async content states
  useEffect(() => {
    onContentResized?.();
  }, [isLoading, subRows, onContentResized]);

  /** Ref used to avoid unnecessary rerenderings of subrows. */
  const parentRowRef = useRef<Row<ETableRow>>(undefined);
  parentRowRef.current = parentRow;

  const expansionReloadKey =
    parentRowRef.current!.original.options?.expansionKey ??
    parentRowRef.current!.original.options?.expansion;

  // load potentially async sub row(s)
  useEffect(() => {
    const getSubRowsData = async () => {
      setIsLoading(true);
      const subRowsResult =
        await parentRowRef.current!.original.options?.expansion?.(
          parentRowRef.current!.original
        );
      setIsLoading(false);
      setSubRows(subRowsResult);
    };
    getSubRowsData();
  }, [parentRowRef, expansionReloadKey]);

  return {isLoading, subRows};
}

export function TBodySubRows({
  activeItem,
  onRowClick,
  onContentResized,
  parent: parentRow,
}: {
  activeItem: ETableActiveItem | undefined;
  onRowClick: ETableRowClickHandler;
  /** Called after the async expansion content (and thus its height) changed. */
  onContentResized?: () => void;
  parent: Row<ETableRow>;
}): React.ReactElement | null {
  const {isLoading, subRows} = useSubRowsContent(parentRow, onContentResized);

  const parentCells = parentRow.getVisibleCells();
  const hasSelectionColumn = parentCells[0]?.column.id === '_selection';
  /** Colspan of main content cell (i.e. without selection and expansion). */
  const mainCellColSpan = parentCells.length - (hasSelectionColumn ? 1 : 0) - 1;

  const actionsHandler: ETableRowActionHandler =
    useActionsHandlerForSubRow(subRows);

  const {t} = useTranslation('lib');

  if (isLoading && !subRows) {
    return (
      <tr className='noHover' data-parent-id={parentRow.original.id}>
        {hasSelectionColumn && <td className='selection' />}
        <td className='expansion' />
        <td colSpan={mainCellColSpan}>
          <ELoading noDelay />
        </td>
      </tr>
    );
  }

  if (!subRows) {
    // avoid flashing of empty content while the useEffect is still running
    return null;
  }

  if (Array.isArray(subRows) && subRows.length === 0) {
    return (
      <tr data-parent-id={parentRow.original.id}>
        {hasSelectionColumn && <td className='selection' />}
        <td className='expansion' />
        <td colSpan={mainCellColSpan} style={{textAlign: 'center'}}>
          {t('placeholder.noContent')}
        </td>
      </tr>
    );
  }

  // one or more additional rows
  if (Array.isArray(subRows)) {
    return (
      <>
        {subRows.map((subRow, subRowIndex) => {
          // A fake row: a copy of the parent with a replaced `original`. Only
          // `original` is ours; the copied methods still act on the parent.
          const subRowWithParent = {...parentRow, original: subRow};
          return (
            <TBodyRow
              // eslint-disable-next-line react/no-array-index-key
              key={`${parentRow.id}-expanded-${subRowIndex}`}
              actions={actionsHandler}
              activeItem={activeItem}
              isDraggable={false}
              parentRow={parentRow}
              row={subRowWithParent}
              subRowIndex={subRowIndex}
              onRowClick={onRowClick}
            />
          );
        })}
      </>
    );
  }

  // there is only a single row available; rename to make it readable
  const subRow = subRows;

  // sub element (mostly a table) that spans the whole width
  if (React.isValidElement(subRow)) {
    return (
      <tr className='noHover' data-parent-id={parentRow.original.id}>
        {hasSelectionColumn && <td className='selection' />}
        <td className='expansion' />
        <td className='subContainer' colSpan={mainCellColSpan}>
          {subRow}
        </td>
      </tr>
    );
  }

  // at this point we can only assume that the subRow is a ETableSubRow<string>
  // but typescript does not understand the typeguard in isValidElement
  // problem described here: https://github.com/microsoft/TypeScript/issues/53178
  const tableSubRow = subRow as ETableSubRow<string>;

  /**
   * Contains a list of *ordered* column values. The sub row's object properties may not
   * be ordered according to the actual column order. But to calculate the colspan, the
   * order is essential.
   */
  const orderedCellsWithValue = parentCells.map((cell) => ({
    id: cell.column.id,
    value: tableSubRow[cell.column.id],
  }));

  // sub element (mostly a table) that only spans certain columns
  return (
    <tr className='noHover' data-parent-id={parentRow.original.id}>
      {parentCells.map((cell, index) => {
        const key = cell.column.id;
        if (cell.column.id === '_selection') {
          return <td key={key} className='selection' />;
        }
        if (cell.column.id === '_expander') {
          return <td key={key} className='expansion' />;
        }
        const subCell = tableSubRow[cell.column.id];
        const rawCellValue = isCellObject(subCell) ? subCell.cell : subCell;
        if (rawCellValue === null) {
          // used with colspan
          return null;
        }
        if (rawCellValue === '' || rawCellValue === undefined) {
          return <td key={key} />;
        }
        return (
          <td
            key={key}
            className='subContainer'
            colSpan={getColSpan(orderedCellsWithValue, index)}
          >
            {rawCellValue as React.ReactNode}
          </td>
        );
      })}
    </tr>
  );
}
